import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, TrendingDown, TrendingUp, Warehouse, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Dashboard() {
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStockItems: 0,
    totalReceipts: 0,
    totalIssues: 0
  });
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [itemsRes, receiptsRes, issuesRes, alertsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/items`, { headers }),
        axios.get(`${BACKEND_URL}/api/stock-receipts`, { headers }),
        axios.get(`${BACKEND_URL}/api/stock-issues`, { headers }),
        axios.get(`${BACKEND_URL}/api/low-stock-alerts`, { headers })
      ]);

      setStats({
        totalItems: itemsRes.data.length,
        lowStockItems: alertsRes.data.length,
        totalReceipts: receiptsRes.data.length,
        totalIssues: issuesRes.data.length
      });
      setLowStockAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Items',
      value: stats.totalItems,
      icon: Package,
      color: 'text-sky-600',
      bg: 'bg-sky-50',
      testId: 'total-items-stat'
    },
    {
      title: 'Low Stock Alerts',
      value: stats.lowStockItems,
      icon: AlertTriangle,
      color: 'text-red-600',
      bg: 'bg-red-50',
      testId: 'low-stock-stat'
    },
    {
      title: 'Total Receipts',
      value: stats.totalReceipts,
      icon: TrendingDown,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      testId: 'total-receipts-stat'
    },
    {
      title: 'Total Issues',
      value: stats.totalIssues,
      icon: TrendingUp,
      color: 'text-slate-600',
      bg: 'bg-slate-50',
      testId: 'total-issues-stat'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-slate-600 mt-2">Overview of your factory inventory</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid={stat.testId}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900 font-heading">{stat.value}</p>
                  </div>
                  <div className={`${stat.bg} ${stat.color} p-3 rounded-xl`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Low Stock Alerts */}
      {lowStockAlerts.length > 0 && (
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="low-stock-alerts-card">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Low Stock Items
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full" data-testid="low-stock-table">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Item Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Current Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Reorder Level</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockAlerts.map((alert) => (
                    <tr 
                      key={alert.item_id} 
                      className="hover:bg-slate-50/50 border-b border-slate-100 last:border-0"
                      data-testid={`low-stock-item-${alert.item_id}`}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">{alert.item_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{alert.category}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-red-700">{alert.current_stock.toFixed(2)} {alert.unit}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{alert.reorder_level.toFixed(2)} {alert.unit}</td>
                      <td className="px-6 py-4">
                        <Badge className="bg-red-50 text-red-700 border-red-200">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Low Stock
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {lowStockAlerts.length === 0 && (
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-8 text-center">
            <Warehouse className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">No low stock alerts. All items are sufficiently stocked.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default Dashboard;